// ./src/components/features/Refuels/FuelGradeSelector/FuelGradeSelector.tsx
import React, { useMemo, useCallback } from "react";
import {
  Combobox,
  InputBase,
  useCombobox,
  Group,
  Text,
  Box,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

export interface FuelGradeSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected fuel grade */
  value?: string | null | undefined;

  /** Callback fired when fuel grade changes: (name, value) */
  onChange?: (name: string, value: string | null) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Additional custom fuel grades to show */
  customGrades?: string[];
}

// ============================================
// Fuel Grade Presets
// ============================================

interface FuelGradePreset {
  value: string;
  labelKey: string;
  iconName: string;
  category: "gasoline" | "diesel" | "alternative";
}

const FUEL_GRADE_PRESETS: FuelGradePreset[] = [
  // Gasoline grades
  {
    value: "regular",
    labelKey: "fuelGrades.regular",
    iconName: "GasStation",
    category: "gasoline",
  },
  {
    value: "midgrade",
    labelKey: "fuelGrades.midgrade",
    iconName: "GasStation",
    category: "gasoline",
  },
  {
    value: "premium",
    labelKey: "fuelGrades.premium",
    iconName: "GasStation",
    category: "gasoline",
  },
  // Diesel
  {
    value: "diesel",
    labelKey: "fuelGrades.diesel",
    iconName: "GasStation",
    category: "diesel",
  },
  // Alternative
  {
    value: "e85",
    labelKey: "fuelGrades.e85",
    iconName: "Leaf",
    category: "alternative",
  },
];

// ============================================
// Component
// ============================================

const FuelGradeSelectorComponent: React.FC<FuelGradeSelectorProps> = ({
  name = "fuelGrade",
  value,
  onChange,
  placeholder,
  required = false,
  error,
  disabled = false,
  size = "md",
  label,
  customGrades = [],
}) => {
  const { t } = useTranslation(["refuels", "common"]);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Default translations
  const defaultPlaceholder =
    placeholder || t("refuels:form.fields.fuelGrade.placeholder");
  const defaultLabel = label ?? t("refuels:form.fields.fuelGrade.label");

  // Build options list with presets and custom grades
  const options = useMemo(() => {
    const allOptions: { value: string; label: string; iconName: string }[] = [];

    // Add presets with translated labels
    FUEL_GRADE_PRESETS.forEach((preset) => {
      allOptions.push({
        value: preset.value,
        label: t(`refuels:${preset.labelKey}`),
        iconName: preset.iconName,
      });
    });

    // Add custom grades if provided
    customGrades.forEach((grade) => {
      // Don't add if already in presets
      if (
        !allOptions.find(
          (opt) => opt.value.toLowerCase() === grade.toLowerCase()
        )
      ) {
        allOptions.push({
          value: grade,
          label: grade,
          iconName: "GasStation",
        });
      }
    });

    return allOptions;
  }, [t, customGrades]);

  // Get display label for current value
  const displayValue = useMemo(() => {
    if (!value) return "";

    // Check if it matches a preset
    const preset = FUEL_GRADE_PRESETS.find(
      (p) => p.value.toLowerCase() === value.toLowerCase()
    );
    if (preset) {
      return t(`refuels:${preset.labelKey}`);
    }

    // Otherwise return the raw value (custom input)
    return value;
  }, [value, t]);

  // Handle option select
  const handleOptionSubmit = useCallback(
    (selectedValue: string) => {
      onChange?.(name, selectedValue);
      combobox.closeDropdown();
    },
    [name, onChange, combobox]
  );

  // Handle custom input change
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value;
      onChange?.(name, newValue || null);
      combobox.openDropdown();
      combobox.updateSelectedOptionIndex();
    },
    [name, onChange, combobox]
  );

  // Handle input blur - keep custom value
  const handleBlur = useCallback(() => {
    combobox.closeDropdown();
  }, [combobox]);

  // Handle input focus
  const handleFocus = useCallback(() => {
    combobox.openDropdown();
  }, [combobox]);

  // Filter options based on current input
  const filteredOptions = useMemo(() => {
    if (!value) return options;

    const searchLower = value.toLowerCase();
    return options.filter(
      (opt) =>
        opt.value.toLowerCase().includes(searchLower) ||
        opt.label.toLowerCase().includes(searchLower)
    );
  }, [options, value]);

  // Render options
  const renderedOptions = useMemo(() => {
    if (filteredOptions.length === 0) {
      // Show the custom value as an option if no matches
      if (value && value.trim()) {
        return (
          <Combobox.Option value={value}>
            <Group gap="sm" wrap="nowrap">
              <AppIcon iconName="Plus" size={18} />
              <Text size="sm">
                {t("common:form.useCustomValue", {
                  value: value,
                  defaultValue: `Use "${value}"`,
                })}
              </Text>
            </Group>
          </Combobox.Option>
        );
      }
      return (
        <Combobox.Empty>
          <Text size="sm" c="dimmed">
            {t("common:form.noOptions")}
          </Text>
        </Combobox.Empty>
      );
    }

    return filteredOptions.map((option) => (
      <Combobox.Option key={option.value} value={option.value}>
        <Group gap="sm" wrap="nowrap">
          <AppIcon iconName={option.iconName} size={18} />
          <Text size="sm">{option.label}</Text>
        </Group>
      </Combobox.Option>
    ));
  }, [filteredOptions, value, t]);

  return (
    <Box>
      <Combobox store={combobox} onOptionSubmit={handleOptionSubmit}>
        <Combobox.Target>
          <InputBase
            label={defaultLabel}
            size={size}
            value={displayValue}
            onChange={handleInputChange}
            onClick={() => combobox.openDropdown()}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={defaultPlaceholder}
            disabled={disabled}
            required={required}
            error={error}
            rightSection={<Combobox.Chevron />}
            rightSectionPointerEvents="none"
            leftSection={<AppIcon iconName="GasStation" size={18} />}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>{renderedOptions}</Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Box>
  );
};

export const FuelGradeSelector = React.memo(FuelGradeSelectorComponent);
